import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea", "preview"]
  static values = {
    previewVisible: { type: Boolean, default: false },
    fieldType: String
  }

  connect() {
    // Only initialize for rich_text fields
    if (this.fieldTypeValue !== 'rich_text') {
      return;
    }

    this.createToolbar()
    this.initializeEditor()
  }

  createToolbar() {
    const toolbar = document.createElement('div')
    toolbar.className = 'markdown-toolbar'

    // Create formatting buttons group
    const formattingGroup = this.createButtonGroup([
      { action: 'bold', title: 'Bold', icon: 'bi bi-type-bold' },
      { action: 'italic', title: 'Italic', icon: 'bi bi-type-italic' }
    ])

    // Create headers group
    const headersGroup = this.createButtonGroup([
      { action: 'heading1', title: 'Header 1', text: 'H1' },
      { action: 'heading2', title: 'Header 2', text: 'H2' },
      { action: 'heading3', title: 'Header 3', text: 'H3' }
    ])

    // Create lists group
    const listsGroup = this.createButtonGroup([
      { action: 'bulletList', title: 'Bullet List', icon: 'bi bi-list-ul' },
      { action: 'numberedList', title: 'Numbered List', icon: 'bi bi-list-ol' }
    ])

    // Create preview group
    const previewGroup = this.createButtonGroup([
      { action: 'togglePreview', title: 'Toggle Preview', icon: 'bi bi-eye', text: 'Preview', className: 'btn-outline-info' }
    ])

    // Add all groups to toolbar
    toolbar.appendChild(formattingGroup)
    toolbar.appendChild(headersGroup)
    toolbar.appendChild(listsGroup)
    toolbar.appendChild(previewGroup)

    // Insert toolbar before textarea
    this.textareaTarget.parentNode.insertBefore(toolbar, this.textareaTarget)
    this.toolbar = toolbar
  }

  createButtonGroup(buttons) {
    const group = document.createElement('div')
    group.className = 'btn-group me-2'
    group.setAttribute('role', 'group')

    buttons.forEach(button => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = `btn btn-sm ${button.className || 'btn-outline-secondary'}`
      btn.setAttribute('data-action', `click->markdown-editor#${button.action}`)
      btn.title = button.title

      if (button.icon) {
        const icon = document.createElement('i')
        icon.className = button.icon
        btn.appendChild(icon)
      }

      if (button.text) {
        if (button.icon) {
          btn.appendChild(document.createTextNode(` ${button.text}`))
        } else {
          btn.textContent = button.text
        }
      }

      group.appendChild(btn)
    })

    return group
  }

  initializeEditor() {
    // Create preview container
    const previewContainer = document.createElement('div')
    previewContainer.className = 'markdown-preview d-none'
    previewContainer.setAttribute('data-markdown-editor-target', 'preview')
    previewContainer.innerHTML = '<div class="preview-content"></div>'

    // Insert preview after textarea
    this.textareaTarget.parentNode.insertBefore(previewContainer, this.textareaTarget.nextSibling)

    // Enhance textarea appearance
    this.textareaTarget.classList.add('markdown-editor-textarea')

    // Add real-time preview updates
    this.textareaTarget.addEventListener('input', () => {
      if (this.previewVisibleValue) {
        this.updatePreview()
      }
    })
  }

  // Formatting actions
  bold() {
    this.toggleWrapSelection('**', '**')
  }

  italic() {
    this.toggleWrapSelection('*', '*')
  }

  heading1() {
    this.toggleLineStart('# ')
  }

  heading2() {
    this.toggleLineStart('## ')
  }

  heading3() {
    this.toggleLineStart('### ')
  }

  bulletList() {
    this.toggleLineStart('- ')
  }

  numberedList() {
    const lines = this.getSelectedLines()
    const textarea = this.textareaTarget

    // Check if any line is already numbered
    const hasNumbering = lines.some(line => /^\d+\.\s+/.test(line.text))

    if (hasNumbering) {
      // Remove numbering
      lines.forEach(line => {
        line.text = line.text.replace(/^\d+\.\s+/, '')
      })
    } else {
      // Add numbering
      lines.forEach((line, index) => {
        line.text = `${index + 1}. ${line.text}`
      })
    }

    this.replaceSelection(lines.map(line => line.text).join('\n'))
  }

  togglePreview() {
    this.previewVisibleValue = !this.previewVisibleValue

    if (this.previewVisibleValue) {
      this.showPreview()
    } else {
      this.hidePreview()
    }
  }

  showPreview() {
    this.updatePreview()
    this.textareaTarget.classList.add('d-none')
    this.previewTarget.classList.remove('d-none')

    // Update button appearance
    const button = this.toolbar?.querySelector('[data-action*="togglePreview"]')
    if (button) {
      button.classList.remove('btn-outline-info')
      button.classList.add('btn-info')
      button.innerHTML = '<i class="bi bi-pencil"></i> Edit'
    }
  }

  hidePreview() {
    this.textareaTarget.classList.remove('d-none')
    this.previewTarget.classList.add('d-none')

    // Update button appearance
    const button = this.toolbar?.querySelector('[data-action*="togglePreview"]')
    if (button) {
      button.classList.remove('btn-info')
      button.classList.add('btn-outline-info')
      button.innerHTML = '<i class="bi bi-eye"></i> Preview'
    }
  }

  updatePreview() {
    const content = this.textareaTarget.value
    const previewContent = this.previewTarget.querySelector('.preview-content')
    previewContent.innerHTML = this.markdownToHtml(content)
  }

  // Toggle formatting methods
  toggleWrapSelection(before, after) {
    const textarea = this.textareaTarget
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = textarea.value.substring(start, end)

    // Check if the selection is already wrapped
    const beforeStart = start - before.length
    const afterEnd = end + after.length

    if (beforeStart >= 0 && afterEnd <= textarea.value.length) {
      const textBefore = textarea.value.substring(beforeStart, start)
      const textAfter = textarea.value.substring(end, afterEnd)

      if (textBefore === before && textAfter === after) {
        // Remove the wrapping
        textarea.setRangeText(selectedText, beforeStart, afterEnd, 'end')
        textarea.focus()
        textarea.setSelectionRange(beforeStart, beforeStart + selectedText.length)
        return
      }
    }

    // Add the wrapping
    const wrappedText = `${before}${selectedText}${after}`
    textarea.setRangeText(wrappedText, start, end, 'end')
    textarea.focus()

    // Position cursor to select just the content
    const newStart = start + before.length
    const newEnd = newStart + selectedText.length
    textarea.setSelectionRange(newStart, newEnd)
  }

  toggleLineStart(prefix) {
    const lines = this.getSelectedLines()
    const textarea = this.textareaTarget

    // Check if any line already has this prefix
    const hasPrefix = lines.some(line => line.text.startsWith(prefix))

    lines.forEach(line => {
      if (hasPrefix) {
        // Remove this prefix
        if (line.text.startsWith(prefix)) {
          line.text = line.text.substring(prefix.length)
        }
      } else {
        // Remove any existing markdown prefixes first, then add new prefix
        line.text = line.text.replace(/^(#{1,6}\s+|-\s+|\d+\.\s+)/, '')
        line.text = prefix + line.text
      }
    })

    this.replaceSelection(lines.map(line => line.text).join('\n'))
  }

  // Utility methods
  wrapSelection(before, after) {
    const textarea = this.textareaTarget
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = textarea.value.substring(start, end)

    const wrappedText = `${before}${selectedText}${after}`

    textarea.setRangeText(wrappedText, start, end, 'end')
    textarea.focus()

    // Position cursor after the inserted text
    const newStart = start + before.length
    const newEnd = newStart + selectedText.length
    textarea.setSelectionRange(newStart, newEnd)
  }

  insertAtLineStart(prefix) {
    const lines = this.getSelectedLines()
    const textarea = this.textareaTarget

    lines.forEach(line => {
      // Remove existing prefix if present
      line.text = line.text.replace(/^(#{1,6}\s*|-\s*|\d+\.\s*)/, '')
      line.text = prefix + line.text
    })

    this.replaceSelection(lines.map(line => line.text).join('\n'))
  }

  getSelectedLines() {
    const textarea = this.textareaTarget
    const value = textarea.value
    const start = textarea.selectionStart
    const end = textarea.selectionEnd

    // Find line boundaries
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    const lineEnd = value.indexOf('\n', end)
    const selectionEnd = lineEnd === -1 ? value.length : lineEnd

    const selectedText = value.substring(lineStart, selectionEnd)
    const lines = selectedText.split('\n')

    return lines.map((text, index) => ({
      text,
      start: lineStart + (index === 0 ? 0 : lines.slice(0, index).join('\n').length + 1),
      end: lineStart + lines.slice(0, index + 1).join('\n').length
    }))
  }

  replaceSelection(replacement) {
    const textarea = this.textareaTarget
    const lines = this.getSelectedLines()
    const start = lines[0].start
    const end = lines[lines.length - 1].end

    textarea.setRangeText(replacement, start, end, 'end')
    textarea.focus()
  }

  // Simple markdown to HTML converter for preview
  markdownToHtml(markdown) {
    let html = markdown
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')

      // Bold and italic
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')

      // Lists
      .replace(/^\s*\* (.+)/gm, '<li>$1</li>')
      .replace(/^\s*- (.+)/gm, '<li>$1</li>')
      .replace(/^\s*\d+\. (.+)/gm, '<li>$1</li>')

      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')

    // Wrap in paragraphs and fix lists
    html = '<p>' + html + '</p>'

    // Fix lists
    html = html.replace(/(<li>.*<\/li>)/g, (match) => {
      return '<ul>' + match.replace(/<li>/g, '').replace(/<\/li>/g, '</li><li>').replace(/<li><\/li>$/, '') + '</ul>'
    })

    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/g, '')
    html = html.replace(/<p><h([1-6])>/g, '<h$1>')
    html = html.replace(/<\/h([1-6])><\/p>/g, '</h$1>')
    html = html.replace(/<p><ul>/g, '<ul>')
    html = html.replace(/<\/ul><\/p>/g, '</ul>')

    return html
  }
}